// src/components/TowerItem.jsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import DilutionInput from '../pages/Analise/DilutionInput';
import {
    presetDiluicaoTorre,
    rotuloPresetDiluicao,
} from '../utils/diluicao';
import {
    Box,
    Typography,
    Button,
    Fade,
    useMediaQuery,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Grid,
    Tooltip,
    Divider,
    TextField,
    Stack,
    Switch,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { vincularProtocolo, atualizarTagTorre,
    definirDiluicaoPadraoTorre,
} from '../infra/api/Torres';
import { ValueFlash } from './ValueFlash';
import { useBootstrap } from '../context/BootstrapContext';
import { SnackbarAlert } from './SnackbarAlert';

import Permitido from '../security/Permitido';
import { usePermissao } from '../security/usePermissao';

// Paleta de tags: tons médios/saturados que se destacam TANTO no tema claro
// quanto no escuro. Sem preto/cinza (somem no dark mode) e sem tons pálidos
// (somem no tema claro e deixam o texto branco do chip ilegível).
const TAG_COLORS = [
    '#1976d2', // Azul
    '#0288d1', // Azul-claro
    '#0097a7', // Ciano
    '#2e7d32', // Verde
    '#558b2f', // Verde-oliva
    '#ed6c02', // Laranja
    '#e64a19', // Laranja-vermelho
    '#d32f2f', // Vermelho
    '#c2185b', // Rosa
    '#9c27b0', // Roxo
    '#5e35b1', // Índigo
    '#546e7a', // Azul-acinzentado
];

const statusMap = {
    ONLINE: { label: 'Ativo', color: '#2e7d32', border: '#66BB6A' },
    ATIVO: { label: 'Ativo', color: '#2e7d32', border: '#66BB6A' },
    EXPERIMENTO_RODANDO: {
        label: 'Amostra em análise',
        color: '#2e7d32',
        border: '#66BB6A',
    },
    LIBERADA: { label: 'Liberado', color: '#0277BD', border: '#4FC3F7' },
    PENDENTE: { label: 'Pendente', color: '#EF6C00', border: '#FFB74D' },
    BLOQUEADA: { label: 'Bloqueado', color: '#C62828', border: '#E57373' },
    INATIVO: { label: 'Inativo', color: '#C62828', border: '#E57373' },
    NEW: { label: 'Novo', color: '#455A64', border: '#B0BEC5' },
    MANUTENCAO: { label: 'Manutenção', color: '#0277BD', border: '#4FC3F7' },
    DESVINCULADA: {
        label: 'Desvinculado',
        color: '#6d4c41',
        border: '#a1887f',
    },
    default: { label: 'Desconhecido', color: '#455A64', border: '#B0BEC5' },
};

const NO_PROTOCOL_VALUE = '__NO_PROTOCOL__';

const resolveStatusTorre = (t) => {
    const raw = t?.status;
    if (raw == null || raw === '') return 'ATIVO';
    return raw;
};

const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch {}
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
};

const getAnalysisIdentifier = (a, index = 0) => {
    if (!a) return '';
    const id = normalizeId(a.id || a._id);
    if (id) return id;
    if (a.name) return String(a.name);
    if (a.nome) return String(a.nome);
    return `analysis-${index}`;
};

const useProtoMap = (protocols) =>
    useMemo(() => {
        const map = new Map();
        (protocols || []).forEach((p) => {
            const ids = [p?.id, p?._id];
            ids.forEach((i) => {
                if (i) map.set(normalizeId(i), p);
            });
        });
        return map;
    }, [protocols]);

const protocolLabel = (p) =>
    p?.name || p?.nome || p?.titulo || p?.codigo || p?.id || p?._id;

const findAnalysis = (protocol, rawId) => {
    if (!protocol?.analysis || !rawId) return null;
    const wanted = String(rawId);
    const normalizedWanted = normalizeId(rawId) || wanted;

    return (
        protocol.analysis.find((a, index) => {
            const id1 = normalizeId(a?.id || a?._id);
            const fallback = a?.name || a?.nome || `analysis-${index}`;
            return (
                id1 === normalizedWanted ||
                String(fallback) === normalizedWanted
            );
        }) || null
    );
};

const FlashChip = React.forwardRef(
    ({ icon, prefix, value, formatValue, sx, pulseOn, ...rest }, ref) => {
        const theme = useTheme();
        const prev = useRef({ v: undefined, p: undefined });
        const [flash, setFlash] = useState(false);

        useEffect(() => {
            const changed =
                prev.current.v !== value || prev.current.p !== pulseOn;
            if (prev.current.v !== undefined && changed) {
                setFlash(true);
                const id = setTimeout(() => setFlash(false), 600);
                return () => clearTimeout(id);
            }
            prev.current = { v: value, p: pulseOn };
        }, [value, pulseOn]);

        const txt =
            value == null
                ? '—'
                : formatValue
                  ? formatValue(value)
                  : String(value);

        return (
            <Chip
                ref={ref}
                {...rest}
                size="small"
                icon={icon}
                variant="outlined"
                sx={{
                    bgcolor: 'background.paper',
                    borderColor: theme.palette.divider,
                    transition:
                        'background-color 0.3s ease-out, border-color 0.3s ease-out',
                    ...(flash && {
                        animation: 'flash-animation 0.6s ease-out',
                    }),
                    ...sx,
                }}
                label={
                    <span>
                        {prefix ? (
                            <strong style={{ marginRight: 6 }}>{prefix}</strong>
                        ) : null}
                        {txt}
                    </span>
                }
            />
        );
    }
);

const InfoRow = React.forwardRef(({ icon, label, value, ...rest }, ref) => (
    <Box
        ref={ref}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
    >
        {icon}
        <Typography
            variant="body2"
            component="span"
            sx={{
                fontWeight: 500,
                color: 'text.secondary',
                minWidth: '120px',
            }}
        >
            {label}:
        </Typography>
        <Typography
            variant="body2"
            component="span"
            color="text.primary"
            sx={{ fontWeight: 600 }}
        >
            {value ?? 'Não informado'}
        </Typography>
    </Box>
));

const TowerItem = ({
    tower,
    protocols,
    live,
    onActionSuccess,
    onShowSnackbar,
    onOpenDashboard,
}) => {
    const theme = useTheme();
    const { pode } = usePermissao();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const { bootstrap } = useBootstrap();
    const canUnlinkProtocol = bootstrap?.config?.nullProtocol === true;

    const lastGoodLiveRef = useRef(null);
    if (live) lastGoodLiveRef.current = live;
    const liveView = live ?? lastGoodLiveRef.current;

    const isRunning = Boolean(tower?.experimentoRodando);

    const [openModalProtocol, setOpenModalProtocol] = useState(false);
    const [selectedProtocolModal, setSelectedProtocolModal] = useState(null);
    const [selectedAnalysisIdModal, setSelectedAnalysisIdModal] = useState('');

    const [openTagModal, setOpenTagModal] = useState(false);
    const [tagInputValue, setTagInputValue] = useState('');
    const [tagColorValue, setTagColorValue] = useState(TAG_COLORS[0]);

    const [localTag, setLocalTag] = useState(tower?.tag || '');
    const [localTagColor, setLocalTagColor] = useState(
        tower?.tagColor || TAG_COLORS[0]
    );

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
    });

    const checkPermission = () => {
        if (!pode('dados.alterar')) {
            setSnackbar({
                open: true,
                message: 'Modo Visitante: Ação de edição bloqueada.',
                severity: 'info',
            });
            return false;
        }
        return true;
    };

    // Diluicao padrao do dispositivo. ESTADO TRIPLO: null (sem preset) != 0 ("pura")
    // != 1..6. Como o DilutionInput nao tem estado nulo, o Switch carrega essa distincao.
    const [openDilutionModal, setOpenDilutionModal] = useState(false);
    const [dilutionEnabled, setDilutionEnabled] = useState(false);
    const [dilutionValue, setDilutionValue] = useState(0);
    const [savingDilution, setSavingDilution] = useState(false);
    const [localPreset, setLocalPreset] = useState(() => presetDiluicaoTorre(tower));

    useEffect(() => {
        if (tower?.tag !== undefined) setLocalTag(tower.tag);
        if (tower?.tagColor !== undefined) setLocalTagColor(tower.tagColor);
        setLocalPreset(presetDiluicaoTorre(tower));
    }, [tower, tower?.tag, tower?.tagColor]);

    let statusAtual = resolveStatusTorre(tower);
    if (
        isRunning &&
        !['DESVINCULADA', 'BLOQUEADA', 'MANUTENCAO', 'DEFEITO'].includes(
            statusAtual
        )
    ) {
        statusAtual = 'EXPERIMENTO_RODANDO';
    }
    const statusInfo = statusMap[statusAtual] || statusMap.default;

    const protoMap = useProtoMap(protocols);

    const protoIdRaw =
        tower?.protocolo?.idProtocolo ||
        tower?.protocolo?.idProtocol ||
        tower?.protocolId ||
        tower?.protocol?._id ||
        tower?.protocol?.id;

    const analysisIdRaw =
        tower?.protocolo?.analiseId ||
        tower?.protocol?.analysisId ||
        tower?.metadata?.lastAnalysis?.analiseId ||
        tower?.ultimaAnalise?.analiseId;

    const protoResolved = useMemo(() => {
        const id = normalizeId(protoIdRaw);
        return id ? protoMap.get(id) || null : null;
    }, [protoIdRaw, protoMap]);

    const analysisResolved = useMemo(
        () =>
            protoResolved ? findAnalysis(protoResolved, analysisIdRaw) : null,
        [protoResolved, analysisIdRaw]
    );

    // Mapeamento das Cores
    const protocolColor = useMemo(() => {
        return protoResolved?.color || statusInfo.border;
    }, [protoResolved, statusInfo.border]);

    const analysisColor = useMemo(() => {
        return analysisResolved?.color || protocolColor;
    }, [analysisResolved, protocolColor]);

    const handleOpenModalProtocol = () => {
        setSelectedProtocolModal(protoResolved);
        if (analysisResolved && protoResolved?.analysis) {
            const idx = protoResolved.analysis.findIndex(
                (a) => a === analysisResolved
            );
            setSelectedAnalysisIdModal(
                getAnalysisIdentifier(analysisResolved, idx)
            );
        } else {
            setSelectedAnalysisIdModal('');
        }
        setOpenModalProtocol(true);
    };

    const handleCloseModalProtocol = () => {
        setOpenModalProtocol(false);
    };

    const lastSyncDate = useMemo(() => {
        if (liveView?.epoch != null) {
            const d = new Date(Number(liveView.epoch) * 1000);
            if (!Number.isNaN(d.getTime())) return d;
        }
        if (liveView?.timestamp) {
            const d = new Date(liveView.timestamp);
            if (!Number.isNaN(d.getTime())) return d;
        }
        if (tower?.ultimaSincronizacao) {
            const d = new Date(tower.ultimaSincronizacao);
            if (!Number.isNaN(d.getTime())) return d;
        }
        return null;
    }, [liveView?.epoch, liveView?.timestamp, tower?.ultimaSincronizacao]);

    const lastSyncEpoch =
        liveView?.epoch ??
        (lastSyncDate ? Math.floor(lastSyncDate.getTime() / 1000) : null);

    const STALE_THRESHOLD_MS = 5 * 60 * 1000;
    const isStale = lastSyncDate
        ? Date.now() - lastSyncDate.getTime() > STALE_THRESHOLD_MS
        : true;

    const handleOpenDilutionModal = () => {
        if (!checkPermission()) return;
        const atual = presetDiluicaoTorre(tower);
        setDilutionEnabled(atual !== null);
        setDilutionValue(atual ?? 0);
        setOpenDilutionModal(true);
    };

    const handleConfirmDilution = async () => {
        if (!checkPermission()) return;
        // Switch desligado => envia null => backend REMOVE o preset.
        const valor = dilutionEnabled ? Number(dilutionValue) : null;
        setSavingDilution(true);
        try {
            await handleApiAction(
                () => definirDiluicaoPadraoTorre(tower?.numeroSerie, valor),
                null,
                valor === null
                    ? 'Diluição padrão removida.'
                    : `Diluição padrão definida (${valor === 0 ? 'pura' : `10⁻${valor}`}).`,
                'definir a diluição padrão'
            );
            setLocalPreset(valor);
            setOpenDilutionModal(false);
        } catch {
            // erro já reportado pelo handleApiAction
        } finally {
            setSavingDilution(false);
        }
    };

    const handleApiAction = async (
        apiFunc,
        payload,
        successMessage,
        errorMessage
    ) => {
        try {
            const data = await apiFunc(payload);
            onShowSnackbar?.({
                open: true,
                message: successMessage,
                severity: 'success',
            });

            onActionSuccess?.(tower?.numeroSerie);
            return data;
        } catch (error) {
            const apiError = error?.message || `Falha ao ${errorMessage}`;
            onShowSnackbar?.({
                open: true,
                message: apiError,
                severity: 'error',
            });
            console.error(`Erro ao ${errorMessage}:`, error);
            throw error;
        }
    };

    const handleConfirmLink = () => {
        if (!checkPermission()) return;
        if (!tower?.id && !tower?._id) return;

        const idProtocoloPayload = selectedProtocolModal
            ? normalizeId(selectedProtocolModal.id || selectedProtocolModal._id)
            : null;

        const analiseIdPayload = selectedProtocolModal
            ? selectedAnalysisIdModal || null
            : null;

        const payload = {
            idTorre: normalizeId(tower.id || tower._id),
            idProtocolo: idProtocoloPayload,
            analiseId: analiseIdPayload,
        };

        handleApiAction(
            vincularProtocolo,
            payload,
            idProtocoloPayload
                ? 'Protocolo vinculado com sucesso!'
                : 'Protocolo desvinculado com sucesso!',
            idProtocoloPayload ? 'vincular protocolo' : 'desvincular protocolo'
        );
        handleCloseModalProtocol();
    };

    const handleOpenTagModal = () => {
        setTagInputValue(localTag);
        // Se a cor salva não faz mais parte da paleta (ex.: preto antigo, já
        // removido), começa no padrão em vez de deixar nenhum swatch marcado.
        setTagColorValue(
            TAG_COLORS.includes(localTagColor) ? localTagColor : TAG_COLORS[0]
        );
        setOpenTagModal(true);
    };

    const handleConfirmTag = async () => {
        if (!checkPermission()) return;
        if (!tower?.id && !tower?._id) return;

        const payload = {
            idTorre: normalizeId(tower.id || tower._id),
            tag: tagInputValue,
            tagColor: tagColorValue,
        };

        try {
            await handleApiAction(
                atualizarTagTorre,
                payload,
                'Tag atualizada com sucesso.',
                'atualizar tag'
            );
            setLocalTag(tagInputValue);
            setLocalTagColor(tagColorValue);
            setOpenTagModal(false);
        } catch (error) {}
    };

    return (
        <>
            <SnackbarAlert
                open={snackbar.open}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                severity={snackbar.severity}
            />

            <Fade in>
                <Paper
                    id={`tower-card-${tower?.numeroSerie}`}
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        pb: 0,
                        mb: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        borderLeft: `5px solid ${protocolColor}`,
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        transition:
                            'transform 0.25s ease-in-out, box-shadow 0.25s ease-in-out, border-color 0.25s',
                        '&:hover': {
                            transform: isDesktop
                                ? 'translateY(-4px)'
                                : 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                            borderColor: alpha(protocolColor, 0.4),
                        },
                    }}
                >
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12}>
                            <Box sx={{ px: { xs: 0, sm: 1 } }}>
                                {/* HEADER DO DISPOSITIVO */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{
                                                fontWeight: '800',
                                                letterSpacing: -0.5,
                                            }}
                                        >
                                            {tower?.numeroSerie}
                                        </Typography>
                                        <Chip
                                            label={statusInfo.label}
                                            size="small"
                                            sx={{
                                                backgroundColor:
                                                    statusInfo.border,
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                        {localTag && (
                                            <Chip
                                                icon={
                                                    <Lucide.Tag
                                                        size={14}
                                                        color="#fff"
                                                    />
                                                }
                                                label={localTag}
                                                size="small"
                                                sx={{
                                                    backgroundColor:
                                                        localTagColor,
                                                    color: '#fff',
                                                    fontWeight: 500,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        )}
                                        {localPreset !== null && (
                                            <Tooltip
                                                title={
                                                    localPreset === 0
                                                        ? 'Diluição padrão: amostra pura (sem diluição)'
                                                        : `Diluição padrão: 10⁻${localPreset}`
                                                }
                                            >
                                                <Chip
                                                    icon={<Lucide.Droplet size={13} />}
                                                    label={rotuloPresetDiluicao(tower)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>

                                {/* BLOCO DE PROTOCOLO VINCULADO / MODO TELEMETRIA — mesma cor/ícone
                                    (Activity, secondary) do botão "Telemetria" no rodapé, pra deixar
                                    visualmente claro que "sem protocolo" é o que libera aquele botão,
                                    e não apenas "nenhuma configuração". */}
                                <Box
                                    sx={{
                                        p: 1.5,
                                        mb: 2,
                                        bgcolor: 'background.default', // Fundo neutro
                                        border: `1px solid ${
                                            protoResolved || protoIdRaw
                                                ? theme.palette.divider
                                                : alpha(
                                                      theme.palette.secondary
                                                          .main,
                                                      0.3
                                                  )
                                        }`,
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1,
                                            color:
                                                protoResolved || protoIdRaw
                                                    ? protoResolved
                                                        ? protocolColor
                                                        : 'text.secondary'
                                                    : theme.palette.secondary
                                                          .main,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {protoResolved || protoIdRaw ? (
                                            <Lucide.FlaskConical
                                                size={16}
                                                color={
                                                    protoResolved
                                                        ? protocolColor
                                                        : theme.palette.text
                                                              .secondary
                                                }
                                            />
                                        ) : (
                                            <Lucide.Activity
                                                size={16}
                                                color={
                                                    theme.palette.secondary
                                                        .main
                                                }
                                            />
                                        )}
                                        {protoResolved || protoIdRaw
                                            ? 'Protocolo Vinculado'
                                            : 'Modo Telemetria Ativo'}
                                    </Typography>

                                    {protoResolved ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: protocolColor,
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {protocolLabel(protoResolved)}
                                            </Typography>

                                            {analysisResolved ? (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: analysisColor,
                                                        fontWeight: 500,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    <Lucide.SlidersHorizontal
                                                        size={16}
                                                        color={analysisColor}
                                                    />{' '}
                                                    {analysisResolved?.name ||
                                                        analysisResolved?.nome ||
                                                        normalizeId(
                                                            analysisResolved?.id ||
                                                                analysisResolved?._id
                                                        )}
                                                </Typography>
                                            ) : analysisIdRaw ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Lucide.SlidersHorizontal size={16} />{' '}
                                                    Análise:{' '}
                                                    {normalizeId(analysisIdRaw)}
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Sem análise selecionada
                                                </Typography>
                                            )}

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    mt: 0.5,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                <Lucide.Info size={12} />
                                                Desvincule o protocolo para
                                                liberar o Modo Telemetria
                                                (leitura e ajuste manual dos
                                                sensores).
                                            </Typography>
                                        </Box>
                                    ) : protoIdRaw ? (
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Protocolo ID:{' '}
                                                {normalizeId(protoIdRaw)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    mt: 0.5,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                <Lucide.Info size={12} />
                                                Desvincule o protocolo para
                                                liberar o Modo Telemetria
                                                (leitura e ajuste manual dos
                                                sensores).
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: theme.palette
                                                        .secondary.main,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                Sem protocolo — leitura manual
                                                disponível
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {liveView && (
                                    <Divider sx={{ my: 2, opacity: 0.5 }} />
                                )}

                                {/* BLOCO DE TELEMETRIA */}
                                {liveView && (
                                    <Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1.5,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: 'text.primary',
                                                }}
                                            >
                                                Monitorização em Tempo Real
                                            </Typography>
                                            {/* Ícone de info da telemetria */}
                                            <Permitido cap="telemetria.ver">
                                                <Tooltip
                                                    title={
                                                        isStale
                                                            ? 'Dados desatualizados'
                                                            : 'Ligação Ativa'
                                                    }
                                                    arrow
                                                >
                                                    <Lucide.Info
                                                        color={
                                                            isStale
                                                                ? theme.palette.warning.main
                                                                : theme.palette.success.main
                                                        }
                                                        size={16}
                                                    />
                                                </Tooltip>
                                            </Permitido>
                                        </Box>

                                        {/* Dados de telemetria detalhados; sem a capacidade, só o aviso */}
                                        <Permitido
                                            cap="telemetria.ver"
                                            fallback={
                                                <Box sx={{ mb: 2 }}>
                                                    <Tooltip title="A telemetria detalhada é exclusiva para planos superiores.">
                                                        <Chip
                                                            icon={
                                                                <Lucide.Lock size={14} />
                                                            }
                                                            label="Telemetria Oculta"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: alpha(
                                                                    theme
                                                                        .palette
                                                                        .grey[300],
                                                                    0.5
                                                                ),
                                                                color: 'text.disabled',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 1,
                                                    mb: 2,
                                                    opacity: isStale ? 0.6 : 1,
                                                    transition:
                                                        'opacity 0.3s ease',
                                                }}
                                            >
                                                <Tooltip
                                                    title="Temperatura da Amostra"
                                                    arrow
                                                >
                                                    <FlashChip
                                                        icon={
                                                            <Lucide.Thermometer size={16} />
                                                        }
                                                        prefix="Sample"
                                                        value={
                                                            liveView
                                                                ?.temperatures
                                                                ?.sample
                                                        }
                                                        formatValue={(v) =>
                                                            `${Number(v).toFixed(2)}°C`
                                                        }
                                                        pulseOn={lastSyncEpoch}
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    title="Temperatura do Núcleo"
                                                    arrow
                                                >
                                                    <FlashChip
                                                        icon={
                                                            <Lucide.Thermometer size={16} />
                                                        }
                                                        prefix="Core"
                                                        value={
                                                            liveView
                                                                ?.temperatures
                                                                ?.core
                                                        }
                                                        formatValue={(v) =>
                                                            `${Number(v).toFixed(2)}°C`
                                                        }
                                                        pulseOn={lastSyncEpoch}
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    title="Temperatura Ambiente"
                                                    arrow
                                                >
                                                    <FlashChip
                                                        icon={
                                                            <Lucide.Thermometer size={16} />
                                                        }
                                                        prefix="Amb"
                                                        value={
                                                            liveView
                                                                ?.temperatures
                                                                ?.ambient
                                                        }
                                                        formatValue={(v) =>
                                                            `${Number(v).toFixed(2)}°C`
                                                        }
                                                        pulseOn={lastSyncEpoch}
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    title="Fonte (Tensão · Corrente)"
                                                    arrow
                                                >
                                                    <FlashChip
                                                        icon={
                                                            <Lucide.Zap size={16} />
                                                        }
                                                        value={`${Number(liveView?.powerSupply?.voltage ?? 0).toFixed(2)}V · ${Number(liveView?.powerSupply?.current ?? 0).toFixed(2)}A`}
                                                        pulseOn={lastSyncEpoch}
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    title="Versão do Firmware"
                                                    arrow
                                                >
                                                    <FlashChip
                                                        icon={
                                                            <Lucide.Cpu size={16} />
                                                        }
                                                        prefix="FW"
                                                        value={
                                                            liveView?.firmwareVersion
                                                        }
                                                        pulseOn={lastSyncEpoch}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </Permitido>
                                    </Box>
                                )}

                                <Divider sx={{ my: 2, opacity: 0.5 }} />

                                <InfoRow
                                    icon={
                                        <Lucide.Calendar
                                            size={16}
                                            color={theme.palette.action.active}
                                        />
                                    }
                                    label="Data de Registo"
                                    value={
                                        tower?.dataCriacao
                                            ? format(
                                                  new Date(tower.dataCriacao),
                                                  'dd/MM/yyyy HH:mm',
                                                  { locale: ptBR }
                                              )
                                            : 'N/A'
                                    }
                                />

                                <InfoRow
                                    icon={
                                        <Lucide.RefreshCw
                                            size={16}
                                            color={theme.palette.action.active}
                                        />
                                    }
                                    label="Última Sinc."
                                    value={
                                        lastSyncEpoch != null ? (
                                            <ValueFlash
                                                value={lastSyncEpoch}
                                                fmt={(ep) =>
                                                    format(
                                                        new Date(
                                                            Number(ep) * 1000
                                                        ),
                                                        'dd/MM/yyyy HH:mm:ss',
                                                        { locale: ptBR }
                                                    )
                                                }
                                            />
                                        ) : (
                                            'N/A'
                                        )
                                    }
                                />
                            </Box>
                        </Grid>

                        {/* RODAPÉ E AÇÕES */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    py: 2,
                                    px: { xs: 0, sm: 1 },
                                    mt: 1,
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {/* Botão Telemetria só com a capacidade telemetria.ver.
                                        Contained (não outlined) + cor secondary: função importante
                                        o suficiente pra ter o mesmo destaque visual do Protocolo,
                                        só que numa cor diferente pra distinguir as duas ações.
                                        Nome/ícone trocados de "Dashboard" pra "Telemetria" — o
                                        nome antigo não deixava claro que ali dentro tem
                                        temperatura, espectrometria, colorimetria e sweep capacitivo
                                        ao vivo, não só um resumo estático. */}
                                    <Permitido cap="telemetria.ver">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<Lucide.Activity size={16} />}
                                            color="secondary"
                                            disableElevation
                                            onClick={() =>
                                                onOpenDashboard?.(
                                                    tower?.numeroSerie
                                                )
                                            }
                                            sx={{ borderRadius: 2 }}
                                        >
                                            Telemetria
                                        </Button>
                                    </Permitido>

                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Lucide.Tag size={16} />}
                                        onClick={handleOpenTagModal}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Editar Tag
                                    </Button>

                                    {pode('dispositivo.diluicaoPadrao') && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Lucide.Droplet size={16} />}
                                            onClick={handleOpenDilutionModal}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            Diluição
                                        </Button>
                                    )}

                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<Lucide.Link size={16} />}
                                        disableElevation
                                        onClick={handleOpenModalProtocol}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Protocolo
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Fade>

            {/* Modal de Protocolo */}
            <Dialog
                open={openModalProtocol}
                onClose={handleCloseModalProtocol}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Vincular Protocolo ao BioAILab {tower?.numeroSerie}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <FormControl fullWidth variant="outlined" size="medium">
                            <InputLabel id="protocol-select-label">
                                Selecione um Protocolo
                            </InputLabel>
                            <Select
                                labelId="protocol-select-label"
                                label="Selecione um Protocolo"
                                value={
                                    selectedProtocolModal
                                        ? normalizeId(
                                              selectedProtocolModal.id ||
                                                  selectedProtocolModal._id
                                          )
                                        : NO_PROTOCOL_VALUE
                                }
                                onChange={(e) => {
                                    const pid = e.target.value;
                                    if (pid === NO_PROTOCOL_VALUE) {
                                        setSelectedProtocolModal(null);
                                        setSelectedAnalysisIdModal('');
                                        return;
                                    }
                                    const protocol = (protocols || []).find(
                                        (p) =>
                                            normalizeId(p.id) ===
                                                normalizeId(pid) ||
                                            normalizeId(p._id) ===
                                                normalizeId(pid)
                                    );
                                    setSelectedProtocolModal(protocol || null);
                                    setSelectedAnalysisIdModal('');
                                }}
                            >
                                {canUnlinkProtocol && (
                                    <MenuItem value={NO_PROTOCOL_VALUE}>
                                        <em>
                                            Nenhum (Desvincular do Aparelho)
                                        </em>
                                    </MenuItem>
                                )}
                                {(protocols || [])
                                    .filter((p) => normalizeId(p.id || p._id))
                                    .map((p) => (
                                        <MenuItem
                                            key={normalizeId(p.id || p._id)}
                                            value={normalizeId(p.id || p._id)}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor:
                                                            p.color || '#999',
                                                    }}
                                                />
                                                {protocolLabel(p)}
                                            </Box>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>

                        {selectedProtocolModal?.analysis?.length > 0 && (
                            <FormControl
                                fullWidth
                                sx={{ mt: 3 }}
                                variant="outlined"
                            >
                                <InputLabel id="analysis-select-label">
                                    Selecione uma Análise Específica
                                </InputLabel>
                                <Select
                                    labelId="analysis-select-label"
                                    label="Selecione uma Análise Específica"
                                    value={selectedAnalysisIdModal}
                                    onChange={(e) =>
                                        setSelectedAnalysisIdModal(
                                            e.target.value
                                        )
                                    }
                                >
                                    {selectedProtocolModal.analysis.map(
                                        (a, index) => {
                                            const id = getAnalysisIdentifier(
                                                a,
                                                index
                                            );
                                            return (
                                                <MenuItem key={id} value={id}>
                                                    {a?.name || a?.nome || id}
                                                </MenuItem>
                                            );
                                        }
                                    )}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseModalProtocol} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmLink}
                        variant="contained"
                        disableElevation
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de TAG */}
            <Dialog
                open={openTagModal}
                onClose={() => setOpenTagModal(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>Editar Tag do BioAILab</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            label="Nome ou Localização"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={tagInputValue}
                            onChange={(e) => setTagInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmTag();
                            }}
                            helperText="Ex: Bancada Principal, Sala 2"
                        />
                        <Box>
                            <Typography
                                variant="subtitle2"
                                gutterBottom
                                color="text.secondary"
                            >
                                Cor de Destaque
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(6, 1fr)',
                                    gap: { xs: 1, sm: 1.5 },
                                }}
                            >
                                {TAG_COLORS.map((color) => {
                                    const selected = tagColorValue === color;
                                    return (
                                        <Box
                                            key={color}
                                            role="button"
                                            aria-label={`Selecionar cor ${color}`}
                                            onClick={() =>
                                                setTagColorValue(color)
                                            }
                                            sx={{
                                                width: '100%',
                                                maxWidth: 40,
                                                aspectRatio: '1 / 1',
                                                mx: 'auto',
                                                borderRadius: '50%',
                                                bgcolor: color,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition:
                                                    'transform 0.15s ease, box-shadow 0.15s ease',
                                                // Anel na própria cor do swatch com um "respiro"
                                                // usando background.paper — funciona nos 2 temas,
                                                // diferente do #ccc fixo de antes.
                                                boxShadow: selected
                                                    ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${color}`
                                                    : `inset 0 0 0 1px ${alpha(
                                                          '#000',
                                                          0.12
                                                      )}`,
                                                transform: selected
                                                    ? 'scale(1.08)'
                                                    : 'none',
                                                '&:hover': {
                                                    transform: 'scale(1.12)',
                                                },
                                            }}
                                        >
                                            {selected && (
                                                <Lucide.Check
                                                    size={18}
                                                    color="#fff"
                                                    strokeWidth={3}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Pré-visualização ao vivo: mostra exatamente como a tag
                            vai aparecer no card com o nome + cor escolhidos. */}
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 0.75, display: 'block' }}
                            >
                                Pré-visualização
                            </Typography>
                            <Chip
                                icon={<Lucide.Tag size={14} color="#fff" />}
                                label={tagInputValue?.trim() || 'Sem nome'}
                                sx={{
                                    bgcolor: tagColorValue,
                                    color: '#fff',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: '#fff' },
                                }}
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setOpenTagModal(false)}
                        color="inherit"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmTag}
                        variant="contained"
                        disableElevation
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diluição padrão do dispositivo (ADMIN/GESTOR).
                O Switch existe porque o estado é TRIPLO: sem preset (null) != "pura" (0)
                != 1..6 — e o DilutionInput sozinho não representa a ausência. */}
            <Dialog
                open={openDilutionModal}
                onClose={savingDilution ? undefined : () => setOpenDilutionModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Lucide.Droplet size={20} />
                        <Box>
                            <Typography fontWeight={700}>Diluição padrão</Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace' }}
                            >
                                {tower?.numeroSerie}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Pré-preenche a diluição deste dispositivo nas novas análises e nos
                        experimentos que ele iniciar. <strong>Não altera análises já criadas.</strong>
                    </Typography>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={dilutionEnabled}
                                onChange={(e) => setDilutionEnabled(e.target.checked)}
                            />
                        }
                        label="Definir diluição padrão"
                    />

                    {dilutionEnabled ? (
                        <Box sx={{ mt: 1 }}>
                            <DilutionInput
                                value={dilutionValue}
                                onChange={setDilutionValue}
                            />
                        </Box>
                    ) : (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            Sem preset — a diluição será escolhida em cada análise.
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setOpenDilutionModal(false)}
                        color="inherit"
                        disabled={savingDilution}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDilution}
                        variant="contained"
                        disableElevation
                        disabled={savingDilution}
                        startIcon={
                            savingDilution ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : null
                        }
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default React.memo(TowerItem);
